import React, { useState, useEffect, useCallback, useContext } from "react";
import { useParams, useHistory } from "react-router-dom";
import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import BackIcon from "@material-ui/icons/ArrowBackIosOutlined";
import MoreIcon from "@material-ui/icons/MoreHorizOutlined";
import clsx from "clsx";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useConfirm } from "../../components/ConfirmDialog";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useMenuStyles } from "../../components/menuStyles";
import Shell from "./Shell";
import UserCounters from "./UserCounters";
import UserConversations from "./UserConversations";
import { useAdminStyles, brl, num, date, dateTime, since } from "./styles";

// Rótulo humano das ações da trilha. O nome técnico fica gravado no banco; a
// tela é lida por gente.
const ACTION_LABEL = {
  "user.set_plan": "Alterou o plano",
  "user.block": "Bloqueou a conta",
  "user.unblock": "Desbloqueou a conta",
  "user.set_staff_role": "Alterou o nível de acesso interno",
  "user.grant_staff": "Concedeu acesso de equipe",
  "user.revoke_staff": "Revogou acesso de equipe",
};

const NIVEL = { support: "Suporte", governance: "Governança" };

const TRIGGER = {
  webhook: "Webhook",
  api_call: "API",
  inbound_call: "Chamada recebida",
  scheduled: "Agendado",
  agent: "Agente",
  tool: "Ferramenta",
};

const SUB_STATUS = {
  active: { label: "Ativa", tone: "ok" },
  pending: { label: "Aguardando pagamento", tone: "warn" },
  past_due: { label: "Em atraso", tone: "bad" },
  canceled: { label: "Encerrada", tone: "neutral" },
};

const INV_STATUS = {
  paid: { label: "Paga", tone: "ok" },
  pending: { label: "Aguardando", tone: "warn" },
  failed: { label: "Não paga", tone: "bad" },
  expired: { label: "Expirada", tone: "bad" },
  canceled: { label: "Cancelada", tone: "neutral" },
};

// Ficha do cliente: o que o suporte precisa ver ANTES de responder — o que ele
// construiu, o que roda, o que gasta, o que falhou e o que deve.
const UserDetail = () => {
  const classes = useAdminStyles();
  const menu = useMenuStyles();
  const { confirm, prompt } = useConfirm();
  const { startViewingAs, user: eu } = useContext(AuthContext);
  const podeVerDinheiro = !!(eu && eu.canSeeMoney);
  const { id } = useParams();
  const history = useHistory();
  const [d, setD] = useState(null);
  const [audits, setAudits] = useState([]);
  const [plans, setPlans] = useState([]);
  const [anchor, setAnchor] = useState(null);
  const [planAnchor, setPlanAnchor] = useState(null);

  const load = useCallback(async () => {
    try {
      const [det, aud] = await Promise.all([
        api.get(`/admin/users/${id}`),
        api.get("/admin/audits", { params: { targetId: id } }),
      ]);
      setD(det.data);
      setAudits(aud.data.audits || []);
    } catch (err) {
      toastError(err);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/plans").then(r => setPlans(r.data.plans || [])).catch(() => {}); }, []);

  // toda ação pede motivo e vai para a trilha — inclusive as reversíveis
  const changePlan = async planCode => {
    setPlanAnchor(null);
    setAnchor(null);
    const reason = await prompt({
      title: "Alterar plano",
      label: "Motivo (fica registrado na auditoria)",
      defaultValue: "",
    });
    if (!reason) return;
    try {
      await api.put(`/admin/users/${id}/plan`, { planCode, reason });
      toast.success("Plano alterado.");
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const block = async () => {
    setAnchor(null);
    const reason = await prompt({ title: "Bloquear conta", label: "Motivo do bloqueio", defaultValue: "" });
    if (!reason) return;
    try {
      await api.post(`/admin/users/${id}/block`, { reason });
      toast.success("Conta bloqueada e sessões encerradas.");
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const unblock = async () => {
    setAnchor(null);
    const ok = await confirm({
      title: "Desbloquear conta",
      message: "O cliente volta a acessar normalmente. A ação fica registrada na auditoria.",
      confirmText: "Desbloquear",
    });
    if (!ok) return;
    try {
      await api.post(`/admin/users/${id}/unblock`, { reason: "" });
      toast.success("Conta desbloqueada.");
      load();
    } catch (err) {
      toastError(err);
    }
  };

  // abre a sessão de visualização: a equipe passa a ver o produto como o
  // cliente. Somente leitura, 30 min, e o motivo vai para a auditoria.
  const verComo = async () => {
    setAnchor(null);
    const reason = await prompt({
      title: "Ver como este cliente",
      label: "Motivo (fica registrado na auditoria)",
      defaultValue: "",
    });
    if (!reason) return;
    try {
      const { data } = await api.post(`/admin/users/${id}/impersonate`, { reason });
      toast.info(`Vendo como ${data.user.name || data.user.email} — somente leitura, ${data.expiresInMinutes} min.`);
      startViewingAs(data.token, data.user);
    } catch (err) {
      toastError(err);
    }
  };

  // nível interno: null (nenhum), suporte ou governança. Só a governança
  // altera níveis, e ninguém altera o próprio — o servidor recusa nos dois casos.
  const definirNivel = async papel => {
    setAnchor(null);
    const ok = await confirm({
      title: papel ? `Tornar ${NIVEL[papel]}` : "Remover acesso interno",
      message: papel === "governance"
        ? "Governança vê tudo — receita, preços, faturas e indicadores do negócio."
        : papel === "support"
        ? "Suporte vê clientes, fluxos, erros e conversas, e pode ver como o cliente. NÃO vê dinheiro: mensalidade, consumo em reais, faturas nem receita."
        : "Esta pessoa deixa de ver o backoffice.",
      confirmText: papel ? "Conceder" : "Remover",
      danger: !papel,
    });
    if (!ok) return;
    try {
      await api.put(`/admin/users/${id}/staff`, { role: papel });
      load();
    } catch (err) {
      toastError(err);
    }
  };

  // override por cliente do max_tokens do copiloto: % do saldo de tokens que UMA
  // resposta pode usar. 0 = volta ao padrão global (50%). Fica na auditoria.
  const definirCopilotPct = async () => {
    setAnchor(null);
    const atual = d.user.copilotTokenPct != null ? Math.round(d.user.copilotTokenPct * 100) : "";
    const val = await prompt({
      title: "Copiloto — limite por conversa",
      label: "% do saldo de tokens que UMA conversa pode consumir (0 = voltar ao padrão 50%)",
      defaultValue: String(atual),
    });
    if (val == null) return;
    const n = Number(String(val).replace(",", "."));
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      toast.error("Informe um número entre 0 e 100.");
      return;
    }
    const pct = n === 0 ? null : n / 100;
    try {
      await api.put(`/admin/users/${id}/copilot-pct`, { pct });
      toast.success(pct == null ? "Copiloto: voltou ao padrão (50%)." : `Copiloto: ${Math.round(pct * 100)}% por conversa.`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  if (!d) {
    return <Shell title="Usuário"><div className={classes.empty}>Carregando…</div></Shell>;
  }

  const { user, subscription, assets, applications, meters, invoices, errors } = d;
  const st = subscription && (SUB_STATUS[subscription.status] || { label: subscription.status, tone: "warn" });
  const consumo = meters.reduce((a, m) => a + m.amountCents, 0);

  return (
    <Shell
      title={user.name || user.email}
      right={
        <>
          <Button size="small" startIcon={<BackIcon style={{ fontSize: 12 }} />} onClick={() => history.push("/admin/users")}>
            Voltar
          </Button>
          <Button size="small" startIcon={<MoreIcon />} onClick={ev => setAnchor(ev.currentTarget)}>
            Ações
          </Button>
          <Menu
            anchorEl={anchor}
            open={!!anchor}
            onClose={() => setAnchor(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ className: menu.paper }}
            MenuListProps={{ disablePadding: true }}
          >
            <div className={menu.sectionLabel}>Suporte</div>
            <MenuItem className={menu.item} onClick={verComo} disabled={!!user.staffRole || !!user.blockedAt}>
              Ver como este cliente
            </MenuItem>
            <div className={menu.divider} />
            <div className={menu.sectionLabel}>Conta</div>
            {podeVerDinheiro && (
              <MenuItem className={menu.item} onClick={ev => setPlanAnchor(ev.currentTarget)}>
                Alterar plano
              </MenuItem>
            )}
            <MenuItem className={menu.item} onClick={definirCopilotPct}>
              Copiloto: limite por conversa
            </MenuItem>
            <div className={menu.sectionLabel}>Acesso interno</div>
            <MenuItem className={menu.item} onClick={() => definirNivel("support")} disabled={user.staffRole === "support"}>
              Tornar Suporte
            </MenuItem>
            <MenuItem className={menu.item} onClick={() => definirNivel("governance")} disabled={user.staffRole === "governance"}>
              Tornar Governança
            </MenuItem>
            {user.staffRole && (
              <MenuItem className={clsx(menu.item, menu.danger)} onClick={() => definirNivel(null)}>
                Remover acesso interno
              </MenuItem>
            )}
            <div className={menu.divider} />
            {user.blockedAt ? (
              <MenuItem className={menu.item} onClick={unblock}>Desbloquear conta</MenuItem>
            ) : (
              <MenuItem className={clsx(menu.item, menu.danger)} onClick={block}>Bloquear conta</MenuItem>
            )}
          </Menu>
          <Menu
            anchorEl={planAnchor}
            open={!!planAnchor}
            onClose={() => setPlanAnchor(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ className: menu.paper }}
            MenuListProps={{ disablePadding: true }}
          >
            <div className={menu.sectionLabel}>Mover para</div>
            {plans.map(p => (
              <MenuItem key={p.code} className={menu.item} onClick={() => changePlan(p.code)} disabled={p.code === user.planCode}>
                {p.name}
              </MenuItem>
            ))}
          </Menu>
        </>
      }
    >
      {user.blockedAt && (
        <div className={classes.card} style={{ borderColor: "#E6B7B7", background: "#FBE9E9" }}>
          <div className={classes.cardTitle}>Conta bloqueada</div>
          <div className={classes.muted}>
            Bloqueada em {dateTime(user.blockedAt)}{user.blockedReason ? ` · ${user.blockedReason}` : ""}
          </div>
        </div>
      )}

      <div className={classes.cards}>
        <div className={classes.kpi}>
          <div className={classes.kpiLabel}>Plano</div>
          <div className={classes.kpiValue} style={{ fontSize: 20 }}>{user.planName || "—"}</div>
          <div className={classes.kpiHint}>
            {st ? st.label : "sem assinatura"}
            {subscription && subscription.currentPeriodEnd ? ` · renova ${date(subscription.currentPeriodEnd)}` : ""}
          </div>
        </div>
        <div className={classes.kpi}>
          <div className={classes.kpiLabel}>Execuções (30d)</div>
          <div className={classes.kpiValue}>{num(applications.reduce((a, x) => a + x.runs30, 0))}</div>
          <div className={classes.kpiHint}>{applications.length} fluxo(s)</div>
        </div>
        {podeVerDinheiro && (
        <div className={classes.kpi}>
          <div className={classes.kpiLabel}>Consumo (30d)</div>
          <div className={classes.kpiValue}>{brl(consumo)}</div>
          <div className={classes.kpiHint}>tarifado por medidor</div>
        </div>
        )}
        <div className={classes.kpi}>
          <div className={classes.kpiLabel}>Último acesso</div>
          <div className={classes.kpiValue} style={{ fontSize: 20 }}>{since(user.lastSeenAt)}</div>
          <div className={classes.kpiHint}>cliente desde {date(user.createdAt)}</div>
        </div>
        <div className={classes.kpi}>
          <div className={classes.kpiLabel}>Copiloto / conversa</div>
          <div className={classes.kpiValue} style={{ fontSize: 20 }}>
            {user.copilotTokenPct != null ? `${Math.round(user.copilotTokenPct * 100)}%` : "padrão"}
          </div>
          <div className={classes.kpiHint}>{user.copilotTokenPct != null ? "override do saldo" : "50% do saldo"}</div>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>Cadastro</div>
        <div className={`${classes.row} ${classes.rowFirst}`}>
          <div className={classes.grow}><div className={classes.muted}>E-mail</div><div className={classes.strong}>{user.email}</div></div>
          <div className={classes.grow}><div className={classes.muted}>CPF/CNPJ</div><div className={classes.strong}>{user.document || "—"}</div></div>
          <div className={classes.grow}><div className={classes.muted}>ID</div><div className={classes.strong}>{user.id}</div></div>
          <div className={classes.grow}>
            <div className={classes.muted}>Acesso interno</div>
            <div className={classes.strong}>{user.staffRole ? NIVEL[user.staffRole] : "—"}</div>
          </div>
          <div className={classes.grow}>
            <div className={classes.muted}>Recursos</div>
            <div className={classes.strong}>
              {assets.numeros} nº · {assets.conectores} conect. · {assets.apiKeys} chave(s) · {assets.agendamentos} agend. · {assets.sms} SMS
            </div>
          </div>
        </div>
      </div>

      <UserCounters userId={id} />

      <UserConversations userId={id} />

      <div className={classes.card}>
        <div className={classes.cardTitle}>Fluxos</div>
        <div className={classes.cardSub}>Ordenados pelo uso nos últimos 30 dias.</div>
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                {["Nome", "Gatilho", "Estado", "Execuções 30d", "Última execução"].map((h, i) => (
                  <th key={h} className={`${classes.th} ${classes.thPlain}`} style={i >= 3 ? { textAlign: "right" } : undefined}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {applications.map(a => (
                <tr key={a.id}>
                  <td className={classes.td}><div className={classes.strong}>{a.name}</div></td>
                  <td className={classes.td}>{TRIGGER[a.triggerType] || a.triggerType || "—"}</td>
                  <td className={classes.td}>
                    <span className={`${classes.pill} ${a.published ? classes.ok : classes.neutral}`}>
                      {a.published ? "Publicado" : "Rascunho"}
                    </span>
                  </td>
                  <td className={`${classes.td} ${classes.tdNum}`}>{num(a.runs30)}</td>
                  <td className={`${classes.td} ${classes.tdNum}`}>{since(a.lastRunAt)}</td>
                </tr>
              ))}
              {applications.length === 0 && (
                <tr><td className={classes.td} colSpan={5}><div className={classes.empty}>Nenhum fluxo criado.</div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {podeVerDinheiro && (
      <div className={classes.cols}>
        <div className={classes.card}>
          <div className={classes.cardTitle}>Consumo por medidor (30d)</div>
          {meters.length === 0 && <div className={classes.empty}>Nada consumido no período.</div>}
          {meters.map((m, i) => (
            <div key={m.meter} className={`${classes.row} ${i === 0 ? classes.rowFirst : ""}`}>
              <div className={classes.grow}>
                <div className={classes.strong}>{m.meter}</div>
                <div className={classes.muted}>{Number(m.quantity).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}</div>
              </div>
              <div className={classes.num}>{brl(m.amountCents)}</div>
            </div>
          ))}
        </div>

        <div className={classes.card}>
          <div className={classes.cardTitle}>Faturas</div>
          {invoices.length === 0 && <div className={classes.empty}>Nenhuma fatura.</div>}
          {invoices.map((inv, i) => {
            const s = INV_STATUS[inv.status] || { label: inv.status, tone: "warn" };
            return (
              <div key={inv.id} className={`${classes.row} ${i === 0 ? classes.rowFirst : ""}`}>
                <div className={classes.grow}>
                  <div className={classes.strong}>{inv.planName || "Assinatura"} · {brl(inv.amountCents)}</div>
                  <div className={classes.muted}>
                    {inv.method === "pix" ? "PIX" : "Cartão"} · {inv.paidAt ? `paga em ${date(inv.paidAt)}` : `vence ${date(inv.dueAt)}`}
                    {inv.lastError ? ` · ${inv.lastError}` : ""}
                  </div>
                </div>
                <span className={`${classes.pill} ${classes[s.tone]}`}>{s.label}</span>
              </div>
            );
          })}
        </div>
      </div>
      )}

      <div className={classes.card}>
        <div className={classes.cardTitle}>Erros recentes</div>
        <div className={classes.cardSub}>É o que costuma explicar o chamado antes de o cliente descrever.</div>
        {errors.length === 0 && <div className={classes.empty}>Nenhum erro registrado.</div>}
        {errors.map((e, i) => (
          <div key={`${e.runId}-${i}`} className={`${classes.row} ${i === 0 ? classes.rowFirst : ""}`}>
            <div className={classes.grow}>
              <div className={classes.strong}>{e.appName} · {e.type}</div>
              <div className={classes.muted}>{e.message}</div>
            </div>
            <div className={classes.muted}>{dateTime(e.createdAt)}</div>
          </div>
        ))}
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>Ações da equipe sobre esta conta</div>
        <div className={classes.cardSub}>Registro imutável — nada aqui é editado ou apagado.</div>
        {audits.length === 0 && <div className={classes.empty}>Nenhuma ação administrativa registrada.</div>}
        {audits.map((a, i) => (
          <div key={a.id} className={`${classes.row} ${i === 0 ? classes.rowFirst : ""}`}>
            <div className={classes.grow}>
              <div className={classes.strong}>{ACTION_LABEL[a.action] || a.action}</div>
              <div className={classes.muted}>
                {a.actorEmail}
                {a.details && a.details.de ? ` · de ${a.details.de} para ${a.details.para}` : ""}
                {a.details && a.details.motivo ? ` · ${a.details.motivo}` : ""}
              </div>
            </div>
            <div className={classes.muted}>{dateTime(a.createdAt)}</div>
          </div>
        ))}
      </div>
    </Shell>
  );
};

export default UserDetail;
